import { useEffect, useRef, useState } from 'react';

/**
 * Tracks which of `count` registered elements is nearest the viewport centre.
 * Used to light up the active timeline node.
 *
 * Usage:
 *   const { setItemRef, activeIndex } = useActiveIndex(items.length);
 *   <li ref={setItemRef(i)} />
 */
export default function useActiveIndex(count) {
  const refs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);

  const setItemRef = (index) => (el) => {
    refs.current[index] = el;
  };

  useEffect(() => {
    const nodes = refs.current.filter(Boolean);
    if (!nodes.length || typeof IntersectionObserver === 'undefined') return undefined;

    // A narrow band across the viewport middle: whatever sits in it is "active".
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const idx = refs.current.indexOf(entry.target);
          if (idx !== -1) setActiveIndex(idx);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [count]);

  return { setItemRef, activeIndex };
}
